import { InspectionOnView } from 'presentation/inspection/inspectionOnView';
import { DocumentsOnInspectionView } from 'presentation/document/documentsToDocumentsOnInspectionView';
import { DocumentsOnTemplateView } from 'presentation/document/documentsOnTemplateView';
import { DocumentOnView } from 'presentation/document/documentOnView';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { ReportTranslationKeys } from 'intl/keys';
import { anyInspectionIssueHasAttachments } from './inspectionIssueAttachmentsAvailability';
import {
  ReportSectionAvailabilityMap,
  ReportSectionId,
  SectionAvailability,
  allSectionsAvailable,
} from './model';

export function hasControlledElementAttachments(
  protocol: ReadonlyArray<{ _id?: string }>,
  inspectionDocuments: DocumentsOnInspectionView
): boolean {
  return protocol.some(
    (item) =>
      item._id != null &&
      (inspectionDocuments[item._id] ?? []).some((doc) => !doc.deleted)
  );
}

function hasNonDeletedDocuments(documents: DocumentOnView[]): boolean {
  return documents.some((doc) => !doc.deleted);
}

function mapHasNonDeletedDocuments(
  documentsByParent: Record<string, DocumentOnView[]>
): boolean {
  return Object.values(documentsByParent).some(hasNonDeletedDocuments);
}

function inspectionSectionAvailability(
  available: boolean,
  unavailableReasonId: string
): SectionAvailability {
  return available
    ? { available: true }
    : { available: false, unavailableReasonId };
}

export type InspectionAvailabilityInput = {
  inspection: InspectionOnView | undefined;
  inspectionTemplateDocuments: DocumentsOnTemplateView;
  inspectionDocuments: DocumentsOnInspectionView;
  /**
   * Issues linked to the inspection (issuesOnInspection store).
   * While issues are still loading, InspectionReportAvailabilityProvider
   * fail-opens instead of calling computeInspectionSectionAvailability.
   */
  issuesOnInspection: IssueModel[];
};

function hasLinkedIssues(linkedIssuesCount: number): boolean {
  return linkedIssuesCount > 0;
}

export function computeInspectionSectionAvailability({
  inspection,
  inspectionTemplateDocuments,
  inspectionDocuments,
  issuesOnInspection,
}: InspectionAvailabilityInput): ReportSectionAvailabilityMap {
  const base = allSectionsAvailable();

  if (!inspection) {
    return base;
  }

  const linkedIssues = hasLinkedIssues(issuesOnInspection.length);
  const hasIssueAttachments =
    anyInspectionIssueHasAttachments(issuesOnInspection);

  const hasGeneralStandard = Boolean(
    inspection.template.generalNorm?.description?.trim()
  );

  const hasSectionDescriptions = inspection.template.sections.some(
    (section) => !section.deleted && Boolean(section.description?.trim())
  );

  const hasControlledElementStandard = inspection.template.checklist.some(
    (item) => !item.deleted && Boolean(item.norm?.description?.trim())
  );

  const hasGeneralStandardAttachments = hasNonDeletedDocuments(
    inspectionTemplateDocuments.templateDocuments
  );

  const hasSectionAttachments = mapHasNonDeletedDocuments(
    inspectionTemplateDocuments.sectionDocuments
  );

  const hasControlledElementStandardAttachments = mapHasNonDeletedDocuments(
    inspectionTemplateDocuments.checklistDocuments
  );

  const hasEkAttachments = hasControlledElementAttachments(
    inspection.protocol,
    inspectionDocuments
  );

  return {
    ...base,
    [ReportSectionId.issueRegister]: inspectionSectionAvailability(
      linkedIssues,
      ReportTranslationKeys.sectionDisabledInspectionIssueRegister
    ),
    [ReportSectionId.generalStandard]: inspectionSectionAvailability(
      hasGeneralStandard,
      ReportTranslationKeys.sectionDisabledInspectionGeneralStandard
    ),
    [ReportSectionId.generalStandardAttachments]: inspectionSectionAvailability(
      hasGeneralStandardAttachments,
      ReportTranslationKeys.sectionDisabledInspectionGeneralStandardAttachments
    ),
    [ReportSectionId.sectionDescriptions]: inspectionSectionAvailability(
      hasSectionDescriptions,
      ReportTranslationKeys.sectionDisabledInspectionSectionDescriptions
    ),
    [ReportSectionId.sectionAttachments]: inspectionSectionAvailability(
      hasSectionAttachments,
      ReportTranslationKeys.sectionDisabledInspectionSectionAttachments
    ),
    [ReportSectionId.controlledElementStandard]: inspectionSectionAvailability(
      hasControlledElementStandard,
      ReportTranslationKeys.sectionDisabledInspectionControlledElementStandard
    ),
    [ReportSectionId.controlledElementStandardAttachments]:
      inspectionSectionAvailability(
        hasControlledElementStandardAttachments,
        ReportTranslationKeys.sectionDisabledInspectionControlledElementStandardAttachments
      ),
    [ReportSectionId.controlledElementAttachments]: inspectionSectionAvailability(
      hasEkAttachments,
      ReportTranslationKeys.sectionDisabledInspectionControlledElementAttachments
    ),
    [ReportSectionId.issueAttachments]: linkedIssues
      ? inspectionSectionAvailability(
          hasIssueAttachments,
          ReportTranslationKeys.sectionDisabledIssueAttachmentsSingle
        )
      : inspectionSectionAvailability(
          false,
          ReportTranslationKeys.sectionDisabledInspectionIssueRegister
        ),
  };
}
